/**
 * The roster card: chrome, sort control, and the region tree (BON-534).
 *
 * The sort options come from the same filtered array as the columns, so a sort
 * by a feature that does not exist is not merely hidden — it is not there to be
 * selected.
 *
 * On a narrow screen the roster scrolls sideways rather than becoming a
 * different component. The criteria allow exactly two differences between the
 * viewports (the calendar, and the sub-region tiles), and every region must
 * still be expandable to its children — a stacked card layout would quietly
 * drop that. Same columns, same rows, same controls, just reachable by
 * scrolling.
 */
import { useState } from 'react';

import type { OverviewRegionRow } from './overview-types';
import { RegionRosterTree } from './RegionRosterTree';
import { byName, ROSTER_SORT_OPTIONS } from './roster-columns';

export interface RegionRosterCardProps {
  rows: OverviewRegionRow[];
  /** The period the figures cover, named in the subtitle. */
  weeks: number;
}

export function RegionRosterCard({ rows, weeks }: RegionRosterCardProps) {
  const [sortId, setSortId] = useState(ROSTER_SORT_OPTIONS[0]?.id);
  const sort = ROSTER_SORT_OPTIONS.find((option) => option.id === sortId);

  // Top-level rows take the chosen sort. Children are always alphabetical:
  // sorting them by revenue would order a handful of sub-regions by a figure
  // nobody is comparing at that level, and the nesting is rebuilt from
  // `parentId` rather than from position, so reordering is safe.
  const topLevel = rows.filter((row) => !row.parentId);
  const rest = [...rows.filter((row) => row.parentId)].sort(byName);
  const sorted = sort ? [...topLevel].sort(sort.compare) : topLevel;
  const ordered = [...sorted, ...rest];

  return (
    <section className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          padding: '14px 18px',
          borderBottom: '1px solid var(--nb-border)',
        }}
      >
        <div>
          <h3 className="nb-display" style={{ margin: 0, fontSize: 14, letterSpacing: '.04em' }}>
            Regions
          </h3>
          <div style={{ fontSize: 11, color: 'var(--nb-subtle)', marginTop: 2 }}>
            {topLevel.length} regions · figures cover the next {weeks} weeks
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {ROSTER_SORT_OPTIONS.length > 1 && (
          <div
            role="group"
            aria-label="Sort regions"
            style={{
              display: 'flex',
              background: 'var(--nb-surface)',
              border: '1px solid var(--nb-border)',
              borderRadius: 999,
              padding: 2,
            }}
          >
            {ROSTER_SORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSortId(option.id)}
                aria-pressed={sortId === option.id}
                style={{
                  height: 22,
                  padding: '0 10px',
                  border: 0,
                  fontSize: 10.5,
                  fontWeight: 600,
                  borderRadius: 999,
                  cursor: 'pointer',
                  color: sortId === option.id ? 'var(--nb-bark-dark)' : 'var(--nb-subtle)',
                  background: sortId === option.id ? 'var(--nb-bg)' : 'transparent',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          <RegionRosterTree rows={ordered} />
        </div>
      </div>
    </section>
  );
}
